import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import toast from 'react-hot-toast';
import { AvailableModel } from 'api/prediction-engine';
import { useAvailableModels } from '../hooks/useAvailableModels';
import { usePredictionStore } from 'stores/predictionStore';

interface PredictionsEngineContextType {
  availableModels: AvailableModel[];
  isLoadingModels: boolean;
  selectedModel: AvailableModel | null;
  setSelectedModel: (model: AvailableModel | null) => void;
}

const PredictionsEngineContext = createContext<
  PredictionsEngineContextType | undefined
>(undefined);

interface PredictionsEngineProviderProps {
  children: ReactNode;
}

export const PredictionsEngineProvider: React.FC<
  PredictionsEngineProviderProps
> = ({ children }) => {
  const { models, isLoading } = useAvailableModels();
  const { predictionResult } = usePredictionStore();
  const [selectedModel, setSelectedModel] = useState<AvailableModel | null>(
    null,
  );

  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      // Restore the model from the stored prediction result
      const storedModelId = predictionResult?.model_id;

      if (storedModelId) {
        const storedModel = models.find((m) => m.id === storedModelId);
        if (storedModel) {
          setSelectedModel(storedModel);
          return;
        } else {
          toast.error(
            `Warning: The model used for these predictions (ID: ${storedModelId}) is no longer available. Please run predictions again with an available model.`,
            { duration: 6000 },
          );
          setSelectedModel(models[0]);
          return;
        }
      }

      setSelectedModel(models[0]);
    }
  }, [models, selectedModel, predictionResult]);

  const value: PredictionsEngineContextType = {
    availableModels: models,
    isLoadingModels: isLoading,
    selectedModel,
    setSelectedModel,
  };

  return (
    <PredictionsEngineContext.Provider value={value}>
      {children}
    </PredictionsEngineContext.Provider>
  );
};

export const usePredictionsEngineContext = (): PredictionsEngineContextType => {
  const context = useContext(PredictionsEngineContext);
  if (context === undefined) {
    throw new Error(
      'usePredictionsEngineContext must be used within a PredictionsEngineProvider',
    );
  }
  return context;
};
